/**
 * ProofStepReveal — staggered entrance for a sequence of proof steps.
 *
 * Wraps each child in a `motion.li` (or `motion.div`) and orchestrates
 * a staggered reveal via the shared `stepContainerVariants` /
 * `stepVariants` from `motionTokens`. Use whenever the consumer wants
 * a list-like surface to read sequentially rather than appear all at
 * once — proof step explanations, witness-predicate evaluations, mode
 * transition trace.
 *
 * The wrapped element defaults to `ol` (semantic ordered list); set
 * `as="div"` for sequences that should not be exposed as a list to
 * assistive tech.
 *
 * Honors `prefers-reduced-motion`: collapses to a static, un-staggered
 * render.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { motion, useReducedMotion } from "motion/react";
import { stepContainerVariants, stepVariants } from "../../styling/motionTokens.js";

export interface ProofStepRevealProps {
  /**
   * Identity of the current sequence. Each new value remounts the
   * reveal, restarting the stagger. Useful when the same slot
   * displays multiple sequences over time.
   */
  readonly triggerKey?: string | number;
  /**
   * Semantic wrapper. `ol`/`ul` expose the children as a list to
   * assistive tech; `div` does not. Defaults to `ol`.
   */
  readonly as?: "ol" | "ul" | "div";
  /** Each child renders as one staggered item. */
  readonly children: React.ReactNode;
  /** Class names on the wrapper. */
  readonly className?: string;
  /** Class names on each child item wrapper. */
  readonly itemClassName?: string;
}

const MotionOl = motion.ol;
const MotionUl = motion.ul;
const MotionDiv = motion.div;
const MotionLi = motion.li;

/**
 * Pull a stable key from a child element. `React.Children.toArray`
 * preserves user-supplied keys (auto-assigning `.0` / `.1` / … when
 * absent), so propagating that key to the wrapper means reorders and
 * inserts don't remount unrelated steps and restart their animations.
 */
function itemKey(child: unknown, index: number): React.Key {
  if (React.isValidElement(child) && child.key !== null) {
    return child.key;
  }
  return `step-${index}`;
}

export const ProofStepReveal = React.memo(function ProofStepReveal({
  triggerKey,
  as = "ol",
  children,
  className,
  itemClassName,
}: ProofStepRevealProps): React.JSX.Element {
  const prefersReducedMotion = useReducedMotion() ?? false;
  const items = React.Children.toArray(children);
  const slotKey = triggerKey ?? "proof-step-reveal";

  const Container = as === "ul" ? MotionUl : as === "div" ? MotionDiv : MotionOl;
  const ItemTag = as === "div" ? MotionDiv : MotionLi;

  if (prefersReducedMotion) {
    return (
      <Container
        key={slotKey}
        className={className}
        data-testid="proof-step-reveal"
        data-trigger-key={String(slotKey)}
      >
        {items.map((child, index) => (
          <ItemTag
            key={itemKey(child, index)}
            className={itemClassName}
            data-testid="proof-step-item"
          >
            {child}
          </ItemTag>
        ))}
      </Container>
    );
  }

  return (
    <Container
      key={slotKey}
      className={className}
      data-testid="proof-step-reveal"
      data-trigger-key={String(slotKey)}
      variants={stepContainerVariants}
      initial="initial"
      animate="enter"
    >
      {items.map((child, index) => (
        <ItemTag
          key={itemKey(child, index)}
          className={itemClassName}
          data-testid="proof-step-item"
          variants={stepVariants}
        >
          {child}
        </ItemTag>
      ))}
    </Container>
  );
});
